/*
 *   Copyright (c) 2021 RCUBE PLANET PVT LTD
 *   All rights reserved.
 */

import React, { createContext, Reducer, useEffect, useMemo, useReducer } from 'react'
import { useCookies } from 'react-cookie'
import { history } from '../history'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { APP_ROUTES, Request, Staff } from '../../src/SubModules/types'

interface stateType {
  isLoading: boolean
  user: Staff | null
  unreadNotification: number
}

interface AuthContextInterface {
  isAuthenticated: boolean
  isLoading: boolean
  login: (param: { email: string; password: string; remember_me: boolean | undefined }) => any
  logout: () => void
  user: Staff | null
  unreadNotification: number
  checkAuth: () => void
  http: InstanceType<typeof Request>['send']
  cookies: {
    [name: string]: any
  }
}
type ReducerAction =
  | { type: 'setIsLoading'; payload: boolean }
  | { type: 'setUser'; payload: Staff }
  | { type: 'removeUser'; payload?: undefined }
  | { type: 'setUnreadNotification'; payload: number }

const initialState: stateType = {
  isLoading: true,
  user: null,
  unreadNotification: 0,
}

const AuthReducer = (state: stateType, action: ReducerAction): stateType => {
  switch (action.type) {
    case 'setIsLoading':
      return {
        ...state,
        isLoading: action.payload,
      }
    case 'setUser':
      return { ...state, user: action.payload }
    case 'removeUser':
      return { ...state, user: null }

    default:
      return state
  }
}

export const AuthContext = createContext<AuthContextInterface>(null as any)
export const Auth: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<stateType, ReducerAction>>(AuthReducer, initialState)
  const [cookies, setCookie, removeCookie] = useCookies(['user_type', 'token'])
  const { isLoading, user, unreadNotification } = state

  const http = useMemo(() => {
    const r = new Request(async (path, headers, body, abortSignal) => {
      const url = path.includes('://') ? path : `${process.env.REACT_APP_API_DOMAIN}${path}`
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
          'Authorization': 'Bearer ' + cookies.token,
          ...headers,
        },
        redirect: 'follow',
        referrerPolicy: 'no-referrer',
        body: JSON.stringify(body),
        signal: abortSignal,
      })
      return await response.json()
    })
    return r.send.bind(r)
  }, [cookies.token])

  const valueProvider: AuthContextInterface = {
    checkAuth: () => {
      dispatch({ type: 'setIsLoading', payload: true })
      console.log('token is ', cookies.token)
      http('ADMIN_AUTH_CHECK', { body: {} })
        .then(async (response) => {
          if (response?.code !== 200) {
            if ((response as any)?.error === 'E_UNAUTHORIZED_ACCESS: ACCOUNT_INACTIVE') {
              alert('Your account is currently under review.')
            }
            throw (response as any)?.errorTrace?.[0]?.message || (response as any).error
          }

          dispatch({ type: 'setUser', payload: response.staff! })
          dispatch({ type: 'setIsLoading', payload: false })
        })
        .catch((err) => {
          if (typeof err !== 'string') {
            err = 'Network error'
          }
          console.warn(err || 'Something went wrong!')

          dispatch({ type: 'setIsLoading', payload: false })
          dispatch({ type: 'removeUser' })
        })
    },
    login: (credentials: APP_ROUTES['ADMIN_AUTH_LOGIN']['request']['validation']) => {
      dispatch({ type: 'setIsLoading', payload: true })
      http('ADMIN_AUTH_LOGIN', { body: credentials })
        .then(async (response) => {
          if (response?.code !== 200) {
            throw (response as any)?.errorTrace?.[0]?.message || (response as any)?.error
          }

          dispatch({ type: 'setIsLoading', payload: false })
          setCookie('token', response?.token.token, { path: '/' })
          history.push('/')
        })
        .catch((err) => {
          if (typeof err !== 'string') {
            err = 'Network error'
          }
          ;(toast as any).warning('Invalid email/password')

          dispatch({ type: 'setIsLoading', payload: false })
          removeCookie('token')
          removeCookie('user_type')
          dispatch({ type: 'removeUser' })
        })
    },
    logout: () => {
      dispatch({ type: 'setIsLoading', payload: true })
      http('ADMIN_AUTH_LOGOUT', { body: {} })
        .then(async (response) => {
          const jsonResult = response as any //TODO:Type Improvised
          if (response?.code !== 200) {
            throw jsonResult?.errorTrace?.[0]?.message || jsonResult?.error
          }
          console.log('Logout successful!')
        })
        .catch((err) => {
          if (typeof err !== 'string') {
            err = 'Network error'
          }
          console.log(err || 'Something went wrong!')
        })
        .finally(() => {
          dispatch({ type: 'setIsLoading', payload: false })
          removeCookie('token')
          dispatch({ type: 'removeUser' })
          history.push('/login')
        })
    },
    http,
    isAuthenticated: !!state.user,
    isLoading,
    user,
    unreadNotification,
    cookies,
  }
  useEffect(() => {
    // alert("check is hitting");
    valueProvider.checkAuth()
  }, [cookies.token])

  return (
    <div>
      <AuthContext.Provider value={valueProvider}>
        <ToastContainer />
        {children}
      </AuthContext.Provider>
    </div>
  )
}
